import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import ContentCard from "@/components/base/ContentCard";
import EmptyState from "@/components/base/EmptyState";
import usePageMeta from "@/hooks/usePageMeta";
import { movieItems, newsItems, postItems, varietyItems } from "@/mocks/content";
import type { AnyContent } from "@/lib/content";
import { formatDateTime } from "@/lib/format";

type SearchTab = "all" | "news" | "movie" | "variety" | "post";

const tabs: { value: SearchTab; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "news", label: "娱乐资讯" },
  { value: "movie", label: "影视推荐" },
  { value: "variety", label: "综艺动态" },
  { value: "post", label: "社区讨论" },
];

const hotKeywords = ["长夜将尽", "雾港", "国庆档", "悬疑", "综艺", "林澈"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") ?? "").trim();
  const [draft, setDraft] = useState(query);
  const [tab, setTab] = useState<SearchTab>("all");

  useEffect(() => {
    setDraft(query);
    setTab("all");
  }, [query]);

  usePageMeta(
    query ? `搜索「${query}」 - 红桃网` : "站内搜索 - 红桃网",
    query
      ? `红桃网站内搜索结果：与「${query}」相关的娱乐资讯、影视推荐、综艺动态与社区讨论。`
      : "在红桃网搜索娱乐资讯、影视推荐、综艺动态与社区讨论，快速找到你想看的内容。",
    "站内搜索,娱乐资讯,影视推荐,综艺动态,红桃网",
  );

  const allContent: AnyContent[] = [...newsItems, ...movieItems, ...varietyItems];

  const matchedContent = useMemo(() => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return allContent.filter(
      (item) =>
        item.title.toLowerCase().includes(lower) ||
        item.summary.toLowerCase().includes(lower) ||
        item.tags.some((tag) => tag.toLowerCase().includes(lower)),
    );
  }, [query]);

  const matchedPosts = useMemo(() => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return postItems.filter(
      (post) =>
        post.title.toLowerCase().includes(lower) ||
        post.excerpt.toLowerCase().includes(lower) ||
        post.content.some((paragraph) => paragraph.toLowerCase().includes(lower)) ||
        post.tags.some((tag) => tag.toLowerCase().includes(lower)),
    );
  }, [query]);

  const counts: Record<SearchTab, number> = {
    all: matchedContent.length + matchedPosts.length,
    news: matchedContent.filter((item) => item.type === "news").length,
    movie: matchedContent.filter((item) => item.type === "movie").length,
    variety: matchedContent.filter((item) => item.type === "variety").length,
    post: matchedPosts.length,
  };

  const visibleContent = tab === "all" || tab === "post" ? matchedContent : matchedContent.filter((item) => item.type === tab);
  const visiblePosts = tab === "all" || tab === "post" ? matchedPosts : [];
  const showContent = tab !== "post";
  const totalVisible = (showContent ? visibleContent.length : 0) + visiblePosts.length;

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value) return;
    setSearchParams({ q: value });
  };

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-portal px-4 pb-7 pt-5 md:px-6 md:pb-9 md:pt-6">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "站内搜索" }]} />
          <p className="mt-5 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">
            <span className="h-3 w-1 rounded-full bg-primary-500" />
            站内搜索
          </p>
          <h1 className="mt-2 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[32px]">
            {query ? `搜索「${query}」` : "站内搜索"}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            覆盖娱乐资讯、影视推荐、综艺动态与社区讨论，共 {counts.all} 条结果。
          </p>

          <form onSubmit={submitSearch} className="mt-4 flex max-w-2xl flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                type="search"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="输入关键词，例如：长夜将尽 / 悬疑 / 综艺"
                aria-label="站内搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <div className="mx-auto w-full max-w-portal px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-wrap items-center gap-1.5">
          {tabs.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setTab(item.value)}
              className={[
                "cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors",
                tab === item.value
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600",
              ].join(" ")}
            >
              {item.label}
              <span className="ml-1 font-label opacity-70">{counts[item.value]}</span>
            </button>
          ))}
        </div>

        {!query ? (
          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-base font-bold text-foreground-950">试试这些热门关键词</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {hotKeywords.map((keyword) => (
                <button
                  key={keyword}
                  type="button"
                  onClick={() => setSearchParams({ q: keyword })}
                  className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-600"
                >
                  {keyword}
                </button>
              ))}
            </div>
            <p className="mt-5 text-xs leading-relaxed text-foreground-400">
              搜索为前端模拟演示，基于站内演示数据的标题、摘要与标签进行匹配。
            </p>
          </div>
        ) : totalVisible === 0 ? (
          <div className="mt-6">
            <EmptyState
              title="没有找到相关内容"
              description={`没有与「${query}」匹配的内容。可以换一个更短的关键词，或者浏览下方推荐频道。`}
              actionLabel="清除搜索"
              onAction={() => setSearchParams({})}
              icon="ri-search-eye-line"
            />
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Link to="/news" className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 hover:text-primary-600">
                浏览娱乐资讯
              </Link>
              <Link to="/recommendations" className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 hover:text-primary-600">
                浏览影视推荐
              </Link>
              <Link to="/variety" className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 hover:text-primary-600">
                浏览综艺动态
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-6">
            {showContent && visibleContent.length > 0 && (
              <section>
                <h2 className="mb-3 flex items-center gap-2 border-b border-background-200 pb-2.5 font-heading text-base font-bold text-foreground-950">
                  <span className="h-3.5 w-1 rounded-full bg-primary-500" />
                  资讯 / 影视 / 综艺
                  <span className="font-label text-xs font-normal text-foreground-400">{visibleContent.length} 条</span>
                </h2>
                <div className="grid grid-cols-1 gap-3">
                  {visibleContent.map((item) => (
                    <ContentCard key={`${item.type}-${item.id}`} item={item} variant="row" showSummaryAlways />
                  ))}
                </div>
              </section>
            )}

            {visiblePosts.length > 0 && (
              <section>
                <h2 className="mb-3 flex items-center gap-2 border-b border-background-200 pb-2.5 font-heading text-base font-bold text-foreground-950">
                  <span className="h-3.5 w-1 rounded-full bg-primary-500" />
                  社区讨论
                  <span className="font-label text-xs font-normal text-foreground-400">{visiblePosts.length} 条</span>
                </h2>
                <div className="space-y-3">
                  {visiblePosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/community/${post.id}`}
                      className="group block rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                    >
                      <p className="font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                        {post.title}
                      </p>
                      <p className="mt-1.5 text-xs leading-relaxed text-foreground-600 line-clamp-2">{post.excerpt}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-400">
                        <span>{post.author}</span>
                        <span className="text-foreground-200">·</span>
                        <span>{formatDateTime(post.createdAt)}</span>
                        <span className="text-foreground-200">·</span>
                        <span>{post.tags.map((tag) => `#${tag}`).join(" ")}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </>
  );
}